import React, { useEffect, useState, useRef } from "react";
import { ROUTES } from "../App";
import { off, onValue, ref } from "firebase/database";
import { database } from "../constants/firebase";
import TypingAnimation from "./TypingAnimation";

const GroupList = ({ filteredGroups, navigate, currentUser }) => {
    const [loadingImages, setLoadingImages] = useState({});
    const [typingGroups, setTypingGroups] = useState({});
    const previousGroupsRef = useRef([]); // Track previous groups to detect changes

    // Listen for typing status in groups
    useEffect(() => {
        if (!currentUser?.uid || filteredGroups.length === 0) return;

        const unsubscribers = [];
        const typingState = {}; // Track typing state locally

        filteredGroups.forEach(group => {
            if (!group.groupId || !group.members) return;

            const memberIds = Object.keys(group.members || {}).filter(
                id => id !== currentUser.uid
            );

            memberIds.forEach(memberId => {
                const typingRef = ref(
                    database,
                    `Users/${memberId}/typingstatus/groups/${group.groupId}/${memberId}`
                );

                const unsubscribe = onValue(typingRef, (snapshot) => {
                    const typingData = snapshot.val();
                    const now = Date.now();
                    const isTyping = typingData?.status && (now - typingData.timestamp < 4000);

                    // Create a unique key
                    const key = `${group.groupId}_${memberId}`;

                    if (isTyping) {
                        typingState[key] = true;
                    } else {
                        delete typingState[key];
                    }

                    // Count typing members for this group
                    const groupTypingCount = Object.keys(typingState)
                        .filter(k => k.startsWith(group.groupId + '_'))
                        .length;

                    setTypingGroups(prev => {
                        if (groupTypingCount === 0) {
                            const { [group.groupId]: _, ...rest } = prev;
                            return rest;
                        } else {
                            return {
                                ...prev,
                                [group.groupId]: groupTypingCount
                            };
                        }
                    });
                });

                unsubscribers.push(unsubscribe);
            });
        });

        return () => {
            unsubscribers.forEach(unsubscribe => unsubscribe());
        };
    }, [currentUser?.uid, filteredGroups]);

    // ✅ Initialize shimmer only when new groups are added
    useEffect(() => {
        // Check if groups have actually changed
        const currentGroupIds = filteredGroups.map(g => g.groupId).sort().join(',');
        const previousGroupIds = previousGroupsRef.current.map(g => g.groupId).sort().join(',');

        if (currentGroupIds !== previousGroupIds) {
            // Only initialize shimmer for new groups that don't have loaded state
            setLoadingImages(prev => {
                const newState = { ...prev };
                filteredGroups.forEach((group) => {
                    // Only set to true if we don't already have a loaded state for this group
                    if (newState[group.groupId] === undefined) {
                        newState[group.groupId] = true;
                    }
                });
                return newState;
            });

            // Update the ref with current groups
            previousGroupsRef.current = filteredGroups;
        }
    }, [filteredGroups]);

    const handleImageLoad = (id) => {
        setLoadingImages((prev) => ({ ...prev, [id]: false }));
    };

    return (
        <>
            {
                filteredGroups.map((group, idx) => {
                    const isTyping = typingGroups[group.groupId];
                    const isLoading = loadingImages[group.groupId];

                    return (
                        <div
                            key={group.groupId || idx} // Use groupId as key instead of index for better performance
                            className="flex items-center justify-between py-4 border-t dark:border-[#D9D9D9]/20 border-gray-200 hover:bg-[#e3eaf3] dark:hover:bg-[#D9D9D9]/10 px-2 hover:cursor-pointer"
                            onClick={() => navigate(`${ROUTES.GROUP_CHAT}/${group.groupId}`)}
                        >
                            <div className="flex items-center gap-4 min-w-0">
                                <div className="relative w-14 h-14 md:w-14 md:h-14 mx-auto flex-shrink-0">
                                    {/* Spinner while loading */}
                                    {isLoading && (
                                        <div className="absolute inset-0 bg-gradient-to-r from-gray-300 via-gray-200 to-gray-300 dark:from-gray-700 dark:via-gray-600 dark:to-gray-700 animate-pulse rounded-full" />
                                    )}
                                    <img
                                        src={
                                            group?.photo ||
                                            "https://firebasestorage.googleapis.com/v0/b/zylen-app.firebasestorage.app/o/group-photo.png?alt=media&token=5109cfab-8e78-43c4-9d86-0ed964c87346"
                                        }
                                        alt={group.name || "Group"}
                                        className={`w-full h-full rounded-full object-cover transition-opacity duration-300 ${isLoading ? "opacity-0" : "opacity-100"
                                            }`}
                                        onLoad={() => handleImageLoad(group.groupId)}
                                        onError={() => handleImageLoad(group.groupId)}
                                    />

                                    {group.status && (
                                        <span className="absolute top-0 right-1 w-3 h-3 bg-green-400 rounded-full border-2 border-[#181A20]" />
                                    )}
                                </div>

                                <div className="flex flex-col min-w-0">
                                    <p className="font-semibold text-base text-black dark:text-white truncate">
                                        {group.name || "Group"}
                                    </p>
                                    {isTyping ? (
                                        <TypingAnimation
                                            className="text-primary text-sm"
                                            text={isTyping === 1 ? "Someone is typing" : `${isTyping} people are typing`}
                                        />
                                    ) : (
                                        <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                                            {group.message || "No message"}
                                        </p>
                                    )}
                                </div>
                            </div>

                            <div className="flex flex-col items-end gap-2 min-w-[80px]">
                                <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                                    {group.displayTime || ""}
                                </div>
                                <div className="flex items-center gap-2">
                                    {group.unread > 0 && !isTyping && (
                                        <span className="bg-[#327691] text-white rounded-full px-2 py-0.5 text-xs font-bold">
                                            {group.unread}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })
            }
        </>
    );
};

export default GroupList;